import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ExternalLink, ArrowRight } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';
import { usePressArticles } from '../../hooks/usePressArticles';

const formatDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const PressPreview: React.FC = () => {
  const { articles, loading } = usePressArticles();
  if (loading || articles.length === 0) return null;

  const featured = articles.filter((a) => a.is_featured);
  const main = featured[0] || articles[0];
  const side = featured.length > 1 ? featured.slice(1, 3) : articles.slice(1, 3);
  const outlets = Array.from(new Set(articles.map((a) => a.outlet))).slice(0, 6);

  if (!main) return null;

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-neutral-50/60 py-14 sm:py-20 lg:py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-[8%] h-72 w-72 rounded-full bg-primary-500/[0.05] blur-3xl" />
        <div className="absolute bottom-0 right-[8%] h-96 w-96 rounded-full bg-neutral-900/[0.025] blur-3xl" />
      </div>
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col items-start gap-4 sm:mb-10 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
          <div>
            <SectionLabel number="12" className="mb-4">
              Ils parlent de nous
            </SectionLabel>
            <h2 className="text-3xl font-black leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-4xl lg:text-5xl">
              Dans la <span className="text-primary-600">presse</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-600 sm:text-base">
              {articles.length}+ articles, {outlets.length} médias partenaires. La couverture média de Proxy en Afrique de l'Ouest.
            </p>
          </div>
          <Link
            to="/newsroom"
            className="group hidden shrink-0 items-center gap-2 rounded-xl border border-neutral-300 bg-white px-5 py-3 text-sm font-bold text-neutral-900 transition-all duration-300 hover:-translate-y-0.5 hover:border-neutral-900 hover:shadow-[0_14px_30px_-12px_rgba(0,0,0,0.15)] sm:inline-flex"
          >
            Toute la Newsroom
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
          </Link>
        </div>

        <div className="mb-8 sm:mb-10">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 sm:gap-4 lg:grid-cols-6 sm:overflow-visible sm:pb-0">
            {outlets.map((o, l) => (
              <div key={l} className="flex h-14 shrink-0 items-center justify-center rounded-xl border border-neutral-200/80 bg-white px-5 py-3 sm:h-16 sm:shrink">
                <span className="text-xs font-black tracking-tight text-neutral-700 whitespace-nowrap sm:text-sm">{o}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
          <a
            href={main.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative col-span-1 overflow-hidden rounded-3xl bg-neutral-950 lg:col-span-7"
          >
            <div className="relative aspect-[16/10] sm:aspect-[16/9] lg:aspect-[16/10]">
              {main.cover_image ? (
                <img src={main.cover_image} alt={main.title} className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-neutral-950" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/70 to-neutral-950/20" />
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                  backgroundSize: '40px 40px',
                }}
              />
              <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-8 lg:p-10">
                <div className="mb-4 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/20 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-primary-300 ring-1 ring-inset ring-primary-400/30 backdrop-blur-sm">
                    À la une
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white/60">{main.outlet}</span>
                </div>
                <h3 className="mb-3 text-xl font-black leading-tight tracking-tight text-white sm:text-2xl lg:text-3xl xl:text-4xl line-clamp-3">{main.title}</h3>
                {main.excerpt && <p className="mb-5 max-w-2xl text-sm leading-relaxed text-white/70 line-clamp-2">{main.excerpt}</p>}
                <div className="flex items-center justify-between gap-4 text-xs">
                  <span className="flex items-center gap-1.5 font-semibold text-white/60">
                    <Calendar className="h-3 w-3" strokeWidth={2.5} />
                    {formatDate(main.published_at)}
                  </span>
                  <span className="inline-flex items-center gap-1.5 font-bold text-white transition-colors group-hover:text-primary-400">
                    Lire l'article
                    <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
                  </span>
                </div>
              </div>
            </div>
          </a>
          <div className="col-span-1 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:col-span-5 lg:grid-cols-1">
            {side.map((o) => (
              <a
                key={o.id}
                href={o.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col justify-between rounded-2xl bg-white p-5 ring-1 ring-neutral-200/80 shadow-[0_8px_30px_-15px_rgba(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_45px_-15px_rgba(0,0,0,0.14)] sm:p-6 lg:flex-1"
              >
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    {o.category && (
                      <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-700 ring-1 ring-inset ring-primary-100">
                        {o.category}
                      </span>
                    )}
                    <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{o.outlet}</span>
                  </div>
                  <h4 className="mb-4 text-base font-black tracking-tight text-neutral-900 line-clamp-3 sm:text-lg">{o.title}</h4>
                </div>
                <div className="flex items-center justify-between border-t border-neutral-100 pt-3 text-xs">
                  <span className="font-semibold text-neutral-500">{formatDate(o.published_at)}</span>
                  <span className="inline-flex items-center gap-1 font-bold text-neutral-900 transition-colors group-hover:text-primary-600">
                    Lire
                    <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" strokeWidth={2.5} />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="mt-6 sm:hidden">
          <Link
            to="/newsroom"
            className="group flex w-full items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-5 py-3 text-sm font-bold text-neutral-900 transition-all active:scale-95"
          >
            Toute la Newsroom
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PressPreview;
